import PagamentoLegadoParcela from '../../../../components/Financeiro/PagamentoLegadoParcela';
import PagamentoLegadoContextual from '../../../../components/Financeiro/PagamentoLegadoContextual';
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { FaArrowLeft, FaThumbsUp, FaSync, FaFileInvoice, FaFileInvoiceDollar, FaFileAlt, FaReceipt, FaExclamationTriangle, FaRegFileAlt, FaMoneyCheckAlt, FaHistory, FaTimes, FaLock, FaRegCommentDots } from 'react-icons/fa';
import { ImCancelCircle } from 'react-icons/im';
import { toast } from 'react-toastify';
import ErpTemplate from '../../../../components/ErpTemplate';
import FormButton from '../../../../components/FormButton';
import api from '../../../../services/api';
import { moeda, copiarTexto } from '../../../../util/util';
import { OrigemMoeda } from '../../../../components/ValorComOrigem';
import { origemDaParcela, formatarCambio } from '../../../../components/moedaOrigemPolicy';
import { useSetFullBreadcrumbTrail } from '../../../../context/BreadcrumbContext';
import { labelFormaPgto, listaDadosPagamento } from './comoPagarPolicy';
import moment from 'moment';
import '../../../../css/financeiro-parcela-pagar.css';
import { mostrarAdiantamentoNaConta, parteAdiantamentoDaConta } from '../../../Orcamento/adiantamentoParcelaPolicy';

function copiarLinhaDigitavel(texto) {
    copiarTexto(texto).then(function (ok) {
        if (ok) { toast.success('Linha digitável copiada.'); }
        else { toast.warning('Não foi possível copiar automaticamente. Selecione o texto e copie.'); }
    });
}

// Tela de DETALHE de uma parcela (Contas a Pagar). Abre ao clicar numa linha da listagem
// ou via deep-link de e-mail (/financeiroParcelaDetalhe/:id). Reaproveita as MESMAS ações
// e endpoints do PainelParcelaPagar (voltar para pendentes / reverter liberação).
// Pagar NÃO fica aqui (D-0097, Rafael 24/10: estava incompleto — sem deduções nem valor
// pago). A baixa é só pelo ⋮ de Contas Pendentes, que tem o modal completo.
export default function DetalheParcelaPagar() {
    const { id } = useParams();
    const history = useHistory();
    const token = localStorage.getItem('token');
    const perfil = localStorage.getItem('perfil');

    const [parcela, setParcela] = useState(null);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState(null);
    // Fase 2 do PLANO-documentos-multiplos: todos os documentos da parcela,
    // por tipo ({ notafiscal: [...], boleto: [...], ... }). Null = endpoint
    // indisponível (compat durante deploy) -> cai nos botões legados.
    const [arquivosParcela, setArquivosParcela] = useState(null);
    const [historico, setHistorico] = useState([]);

    // Modais no padrão .fin-pp (substituíram o Alert/SweetAlert, que destoava
    // do design system e montava formulário com string de HTML).
    // modalConfirm: { tom, titulo, sub, corpo, labelOk, onOk }. Sem `onOk` = só aviso.
    const [modalConfirm, setModalConfirm] = useState(null);

    const carregarParcela = useCallback(() => {
        setCarregando(true);
        api({ method: 'get', action: '/financeiroParcela/' + id, token: token })
            .then((response) => {
                const dado = response.data?.data ?? response.data;
                if (!dado || !dado.id) {
                    setErro('Parcela não encontrada.');
                    setParcela(null);
                } else {
                    setParcela(dado);
                    setErro(null);
                }
            })
            .catch((error) => {
                const m = error.response?.data?.error ?? error.response?.data?.message ?? 'Erro ao carregar a parcela.';
                setErro(m);
                setParcela(null);
            })
            .finally(() => setCarregando(false));

        // Documentos múltiplos (não bloqueia a tela: falhou -> botões legados)
        api({ method: 'get', action: '/financeiroParcela/' + id + '/arquivos', token: token })
            .then((response) => setArquivosParcela(response.data?.data ?? null))
            .catch(() => setArquivosParcela(null));

        // Histórico (também não bloqueia: sem ele a tela segue igual a antes)
        api({ method: 'get', action: '/financeiroParcela/' + id + '/historico', token: token })
            .then((response) => setHistorico(response.data?.data ?? []))
            .catch(() => setHistorico([]));
    }, [id, token]);

    useEffect(() => { carregarParcela(); }, [carregarParcela]);

    // ── derivações de exibição ───────────────────────────────────────────────
    const situacaoAberta = parcela && (parcela.situacao === '1' || parcela.situacao === 1);
    const situacaoPaga = parcela && (parcela.situacao === '2' || parcela.situacao === 2);
    const isUrgente = parcela && (parcela.pagamentourgente === true || parcela.pagamentourgente === 1 || parcela.pagamentourgente === '1');
    const isAprovada = parcela && parcela.dataAprovacaoDiretor;
    const isReprovada = parcela && parcela.dataReprovacaoDiretor;
    const liberadoFinanceiro = parcela && !!parcela.liberado_financeiro_at;
    const podeReverter = ['admin', 'diretor', 'gerentefinanceiro', 'financeiro', 'financeiro.credito', 'financeiro.adiantamento'].includes(perfil);

    const eventoNome = parcela && (
        parcela.titulo?.evento?.nome
        || parcela.titulo?.orcamento_item?.[0]?.orcamento?.evento?.nome
        || parcela.titulo?.venda?.evento?.nome
        || '—'
    );
    const eventoCodigo = parcela && (
        parcela.titulo?.evento?.codigo
        || parcela.titulo?.orcamento_item?.[0]?.orcamento?.evento?.codigo
        || null
    );
    const clienteNome = parcela && (
        parcela.titulo?.evento?.cliente?.nome
        || parcela.titulo?.orcamento_item?.[0]?.orcamento?.evento?.cliente?.nome
        || parcela.titulo?.venda?.cliente?.nome
        || '—'
    );
    const fornecedorNome = parcela && (
        parcela.titulo?.orcamento_item?.[0]?.fornecedor?.nome
        || parcela.participante?.nome
        || '—'
    );
    const fornecedorDoc = parcela && (parcela.participante?.cnpj || parcela.participante?.cpf || null);
    const planoContas = parcela && parcela.conta ? (parcela.conta.caminho + ' - ' + parcela.conta.nome) : '—';

    // "Como pagar" preenchido no Efetivo (forma + dados bancários/PIX/link)
    const formaPgto  = parcela ? labelFormaPgto(parcela.efetivo_forma) : '';
    const dadosPgto  = parcela ? listaDadosPagamento(parcela.efetivo_dados_pagamento) : [];

    // Situação do vencimento: mesma leitura da listagem (vencida / hoje / a vencer)
    const vencInfo = (function () {
        if (!parcela || !parcela.vencimento) return null;
        const d = moment(parcela.vencimento);
        if (!d.isValid()) return { texto: 'Data inválida', tom: 'is-danger', sub: '' };
        if (situacaoPaga) return { texto: d.format('DD/MM/YYYY'), tom: '', sub: 'Já pago' };
        const dias = d.startOf('day').diff(moment().startOf('day'), 'days');
        if (dias < 0)  return { texto: d.format('DD/MM/YYYY'), tom: 'is-danger', sub: 'Vencida há ' + Math.abs(dias) + (Math.abs(dias) === 1 ? ' dia' : ' dias') };
        if (dias === 0) return { texto: d.format('DD/MM/YYYY'), tom: 'is-warn', sub: 'Vence hoje' };
        return { texto: d.format('DD/MM/YYYY'), tom: dias <= 7 ? 'is-warn' : '', sub: 'Em ' + dias + (dias === 1 ? ' dia' : ' dias') };
    })();

    const totalParcelas = parcela ? (parseInt(parcela.parcelas, 10) || 1) : 1;
    const numeroParcela = parcela ? ((parseInt(parcela.ordem, 10) || 0) + 1) : 1;

    const setFullTrail = useSetFullBreadcrumbTrail();
    useEffect(() => {
        setFullTrail([
            { path: '/financeiro', label: 'Financeiro' },
            { path: '/financeiroParcelaPagar', label: 'Parcelas a Pagar' },
            { path: '/financeiroParcelaDetalhe/' + id, label: 'Detalhe da conta' },
        ]);
    }, [setFullTrail, id]);

    // ── ações (mesmos endpoints do PainelParcelaPagar) ───────────────────────
    // Fase 3 do PLANO-documentos-multiplos: remove um documento da parcela.
    // Soft delete no backend — o arquivo NÃO é apagado do storage. Parcela paga
    // só ADMIN/Diretor (a trava é do backend; aqui só mostramos o retorno).
    const acaoRemoverArquivo = (doc, rotuloTipo) => {
        setModalConfirm({
            tom: 'danger',
            titulo: 'Remover ' + rotuloTipo,
            sub: doc.nome || 'anexo',
            corpo: <>
                O documento <strong>{doc.nome || 'anexo'}</strong> deixará de constar nesta parcela.
                <div className="fin-pp__cell-muted" style={{ marginTop: 8 }}>
                    O arquivo continua guardado — nada é apagado definitivamente.
                </div>
            </>,
            labelOk: <><ImCancelCircle size={16} /> Remover documento</>,
            onOk: () => api({ method: 'delete', action: '/financeiroParcela/' + parcela.id + '/arquivos/' + doc.id, token: token })
                .then(() => { toast.success('Documento removido.'); carregarParcela(); })
                .catch((error) => {
                    const m = error.response?.data?.error ?? error.response?.data?.message ?? 'Erro ao remover o documento.';
                    toast.error(m);
                }),
        });
    };

    const acaoVoltarPendentes = () => {
        setModalConfirm({
            tom: 'warn',
            titulo: 'Voltar para Contas Pendentes',
            sub: parcela.descricao,
            corpo: <>A parcela voltará para <strong>Contas Pendentes</strong>: o pagamento será estornado e o saldo do caixa restaurado.</>,
            labelOk: <><FaSync size={16} /> Voltar para Pendentes</>,
            onOk: () => api({ method: 'put', action: '/financeiroParcela/estornar/' + parcela.id, token: token, data: {} })
                .then(() => { toast.success('Parcela retornada para Contas Pendentes.'); carregarParcela(); })
                .catch((error) => {
                    const m = error.response?.data?.error ?? error.response?.data?.message ?? 'Erro desconhecido';
                    toast.error('Erro ao voltar para pendentes: ' + m);
                }),
        });
    };

    const acaoReverterLiberacao = () => {
        // O Alert antigo invertia os botões (confirmButton='Sair', a ação real no
        // denyButton) — o botão azul de destaque era o de cancelar. Aqui o botão
        // de ação é o de ação e ponto.
        setModalConfirm({
            tom: 'warn',
            titulo: 'Remover de Contas Pendentes',
            sub: parcela.descricao,
            corpo: parcela.liberado_financeiro_at
                ? <>O item voltará para <strong>Aprovados pelo Financeiro</strong> no Custo Previsto e precisará ser liberado novamente. Se houver complemento do orçamento aguardando, ele é somado a esta parcela e ela volta para aprovação.</>
                : <>Esta conta não veio de uma Previsão de Pagamento. Ela vai para a aba <strong>Cancelado</strong> (com o motivo) e o mesmo valor entra no <strong>Custo Previsto</strong>, aguardando aprovação — sem fornecedor, entra como "(Fornecedor a definir)".</>,
            labelOk: <><ImCancelCircle size={16} /> Voltar para Custo Previsto</>,
            onOk: () => api({ method: 'put', action: '/financeiroParcela/' + parcela.id + '/reverterLiberacao', token: token, data: {} })
                .then((res) => {
                    toast.success((res && res.data && res.data.msg) || 'Item revertido para Custo Previsto.');
                    // Conta sem Previsão foi cancelada (o valor foi para o Custo Previsto): volta para a lista.
                    if (res && res.data && res.data.devolvida) history.push('/financeiroParcelaPagar'); else carregarParcela();
                })
                .catch((err) => { toast.error((err && err.response && err.response.data && err.response.data.error) || 'Erro ao reverter item.'); }),
        });
    };

    // ── render ────────────────────────────────────────────────────────────────
    // Linha de dado (rótulo à esquerda, valor à direita). O visual do rótulo vem
    // do modificador .fin-pp__tbl--kv no CSS — nada de estilo inline repetido.
    const Linha = ({ label, children }) => (
        <tr>
            <td>{label}</td>
            <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{children}</td>
        </tr>
    );

    const arquivoApi = process.env.REACT_APP_API_URL + '/financeiroParcelaArquivo/';

    return (
        <ErpTemplate>
            <div className="main-content container-fluid fin-pp">
                <div className="fin-pp__head">
                    <div>
                        <h1 className="fin-pp__title">Detalhe da conta a pagar</h1>
                        <p className="fin-pp__subtitle">
                            {parcela ? (parcela.descricao || 'Conta a pagar') : 'Carregando…'}
                        </p>
                    </div>
                    <div className="fin-pp__actions">
                        <button type="button" className="fin-pp__btn" onClick={() => history.goBack()}>
                            <FaArrowLeft size={15} /> Voltar
                        </button>
                        {parcela && situacaoAberta && podeReverter && (
                            <button type="button" className="fin-pp__btn" onClick={acaoReverterLiberacao}>
                                <ImCancelCircle size={15} /> Voltar para Custo Previsto
                            </button>
                        )}
                        {parcela && situacaoPaga && (
                            <button type="button" className="fin-pp__btn" onClick={acaoVoltarPendentes}>
                                <FaSync size={15} /> Voltar para Pendentes
                            </button>
                        )}
                    </div>
                </div>

                {carregando && (
                    <div className="fin-pp__card">
                        <div className="fin-pp__empty" style={{ padding: '48px 0' }}>Carregando…</div>
                    </div>
                )}

                {!carregando && erro && (
                    <div className="fin-pp__card">
                        <div className="fin-pp__empty" style={{ padding: '40px 0' }}>
                            <FaExclamationTriangle size={26} style={{ color: 'var(--fin-wn)' }} />
                            <div style={{ marginTop: 8 }}>{erro}</div>
                        </div>
                    </div>
                )}

                {!carregando && parcela && (<>
                    {/* ── KPIs: o essencial sem precisar ler a tabela ─────────────
                        `.fin-pp__kpis` não tem margem própria — sem o marginBottom
                        a primeira seção cola no card do KPI. */}
                    <section className="fin-pp__kpis" style={{ marginBottom: 18 }}>
                        <div className="fin-pp__kpi">
                            <div className="fin-pp__kpi-top">
                                <span className="fin-pp__kpi-label">Valor</span>
                                <span className="fin-pp__kpi-ic"><FaMoneyCheckAlt size={16} /></span>
                            </div>
                            <div className="fin-pp__kpi-value">R$ {moeda(parcela.valor)}</div>
                            {/* Item 15: moeda, câmbio (congelado na aprovação) e valor na moeda de origem. */}
                            <OrigemMoeda origem={origemDaParcela(parcela)} comBrl />
                            {/* Previsão Inter (28/09): aprovada com a cotação do banco — de onde vem o total. */}
                            {parcela.cambio_banco && (
                                <div className="fin-pp__kpi-sub" style={{ lineHeight: 1.5 }}
                                    title={parcela.cambio_banco.observacao ? 'Observação da aprovação: ' + parcela.cambio_banco.observacao : undefined}>
                                    Cotação do banco {formatarCambio(parcela.cambio_banco.cotacao)}
                                    {parcela.cambio_banco.cambio_inicial ? ' (orçamento ' + formatarCambio(parcela.cambio_banco.cambio_inicial) + ')' : ''}
                                    {' · IOF R$ ' + moeda(parcela.cambio_banco.iof_brl) + ' · IR R$ ' + moeda(parcela.cambio_banco.ir_brl) + ' · taxa R$ ' + moeda(parcela.cambio_banco.taxa_brl)}
                                    {' · plano R$ ' + moeda(parcela.cambio_banco.total_brl)}
                                    {parcela.cambio_banco.observacao ? <div>Obs.: {parcela.cambio_banco.observacao}</div> : null}
                                    {parcela.cambio_banco.aprovado_em ? <div>Câmbio aprovado pelo Eventos{parcela.cambio_banco.aprovado_por ? ' (' + parcela.cambio_banco.aprovado_por + ')' : ''} — pagar pela invoice.</div> : null}
                                    {(parcela.cambio_banco.invoices || []).map((inv) => (
                                        <div key={inv.id}>
                                            <button type="button" className="fin-pp__linkbtn" onClick={() => window.open(process.env.REACT_APP_API_URL + '/arquivo/download/' + inv.arquivo_id, '_blank')}>📎 Invoice: {inv.nome}</button>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <div className="fin-pp__kpi-sub">
                                {totalParcelas > 1 ? 'Parcela ' + numeroParcela + ' de ' + totalParcelas : 'Parcela única'}
                            </div>
                        </div>
                        <div className="fin-pp__kpi">
                            <div className="fin-pp__kpi-top">
                                <span className="fin-pp__kpi-label">Vencimento</span>
                                <span className="fin-pp__kpi-ic"><FaHistory size={16} /></span>
                            </div>
                            <div className={'fin-pp__kpi-value ' + (vencInfo ? vencInfo.tom : '')}>{vencInfo ? vencInfo.texto : '—'}</div>
                            <div className="fin-pp__kpi-sub">{vencInfo ? vencInfo.sub : 'Sem data'}</div>
                        </div>
                        <div className="fin-pp__kpi">
                            <div className="fin-pp__kpi-top">
                                <span className="fin-pp__kpi-label">Situação</span>
                                <span className={'fin-pp__kpi-ic' + (situacaoPaga ? ' is-ok' : '')}><FaThumbsUp size={16} /></span>
                            </div>
                            <div className={'fin-pp__kpi-value ' + (situacaoPaga ? 'is-ok' : 'is-warn')} style={{ fontSize: '1.15rem' }}>
                                {situacaoPaga ? 'Pago' : 'Aberto'}
                            </div>
                            <div className="fin-pp__kpi-sub">
                                {situacaoPaga
                                    ? (parcela.pagamento ? 'Pago em ' + moment(parcela.pagamento).format('DD/MM/YYYY') : 'Pago')
                                    : (liberadoFinanceiro ? 'Liberado para o Financeiro' : 'Aguardando liberação')}
                            </div>
                        </div>
                        <div className="fin-pp__kpi">
                            <div className="fin-pp__kpi-top">
                                <span className="fin-pp__kpi-label">Forma de pagamento</span>
                                <span className="fin-pp__kpi-ic"><FaRegFileAlt size={16} /></span>
                            </div>
                            <div className="fin-pp__kpi-value" style={{ fontSize: '1.15rem' }}>{formaPgto || (parcela.cambio_banco && parcela.cambio_banco.aprovado_em ? 'Invoice (exterior)' : '—')}</div>
                            <div className="fin-pp__kpi-sub">{formaPgto ? 'Definida no Efetivo' : (parcela.cambio_banco && parcela.cambio_banco.aprovado_em ? 'Câmbio aprovado pelo Eventos — pagar pela invoice' : 'Não informada no Efetivo')}</div>
                        </div>
                    </section>

                    <PagamentoLegadoParcela info={parcela.pagamento_legado} onAtualizado={carregarParcela} />
                    <PagamentoLegadoContextual previsoes={parcela.pagamentos_legados_do_plano} />

                    {/* Faixas de estado que não cabem em KPI */}
                    {(isUrgente || isAprovada || isReprovada) && (
                        <div className="fin-pp__statusbar" style={{ marginBottom: 14 }}>
                            {isUrgente   && <span className="fin-pp__pill is-danger">⚠ Urgente</span>}
                            {isAprovada  && <span className="fin-pp__pill is-ok">Aprovado pelo diretor</span>}
                            {isReprovada && <span className="fin-pp__pill is-danger">Reprovado pelo diretor</span>}
                        </div>
                    )}

                    {/* ── Dados da conta ──────────────────────────────────────────
                        ATENÇÃO: `.fin-pp__region` é só um wrapper — no CSS ele é
                        `background: transparent; border: 0; box-shadow: none`. Quem
                        desenha a caixa é o `.fin-pp__card` de dentro (é assim no
                        PainelParcelaPagar). Sem o card, as seções ficam soltas e não
                        dá para ver onde uma começa e a outra termina. */}
                    <section className="fin-pp__region" style={{ marginBottom: 18 }}>
                        <div className="fin-pp__cphead">
                            <span className="fin-pp__cphead-ic"><FaFileAlt size={18} /></span>
                            <div>
                                <h2 className="fin-pp__cphead-title">Dados da conta</h2>
                                <p className="fin-pp__cphead-sub">Origem, cliente e classificação desta parcela</p>
                            </div>
                        </div>
                        <div className="fin-pp__card" style={{ overflow: 'hidden' }}>
                            <div className="fin-pp__tblwrap">
                                <table className="fin-pp__tbl fin-pp__tbl--kv">
                                    <tbody>
                                        <Linha label="Descrição"><strong>{parcela.descricao || '—'}</strong></Linha>
                                        <Linha label="Cliente">{clienteNome}</Linha>
                                        <Linha label="Evento">
                                            {eventoNome}
                                            {eventoCodigo ? <span className="fin-pp__minitag is-info" style={{ marginLeft: 6 }}>#{eventoCodigo}</span> : null}
                                        </Linha>
                                        <Linha label="Fornecedor">
                                            {fornecedorNome}
                                            {fornecedorDoc ? <div className="fin-pp__cell-muted">{fornecedorDoc}</div> : null}
                                        </Linha>
                                        <Linha label="Plano de Contas">
                                            {planoContas === '—'
                                                ? <span className="fin-pp__cell-muted">Não classificado</span>
                                                : planoContas}
                                        </Linha>
                                        {parcela.especie?.nome && (<Linha label="Espécie">{parcela.especie.nome}</Linha>)}
                                        {parcela.observacao && (<Linha label="Observação">{parcela.observacao}</Linha>)}
                                        {isReprovada && parcela.motivoReprovacaoDiretor && (
                                            <Linha label="Motivo da reprovação">
                                                <span style={{ color: 'var(--fin-dg)' }}>{parcela.motivoReprovacaoDiretor}</span>
                                            </Linha>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </section>

                    {/* ── Como pagar ──────────────────────────────────────────────
                        Sem `.fin-pp__cphead-pill` aqui: ele tem `margin-left:auto` e,
                        sem modificador (is-warn), fica sem fundo — virava um texto
                        solto na outra ponta da tela. A forma já é KPI e é a 1ª linha. */}
                    <section className="fin-pp__region" style={{ marginBottom: 18 }}>
                        <div className="fin-pp__cphead">
                            <span className="fin-pp__cphead-ic"><FaMoneyCheckAlt size={18} /></span>
                            <div>
                                <h2 className="fin-pp__cphead-title">Como pagar</h2>
                                <p className="fin-pp__cphead-sub">Forma e dados bancários preenchidos pelo operador no Efetivo</p>
                            </div>
                        </div>
                        <div className="fin-pp__card" style={{ overflow: 'hidden' }}>
                            {(!formaPgto && dadosPgto.length === 0 && !mostrarAdiantamentoNaConta(parcela.adiantamento_fornecedor)) ? (
                                <div className="fin-pp__empty" style={{ padding: '26px 0' }}>
                                    Nenhuma forma de pagamento informada no Efetivo.
                                </div>
                            ) : (
                                <div className="fin-pp__tblwrap">
                                    <table className="fin-pp__tbl fin-pp__tbl--kv">
                                        <tbody>
                                            <Linha label="Forma">{formaPgto || '—'}</Linha>
                                            {dadosPgto.map(function (par, i) {
                                                var rotulo = par[0], valor = par[1];
                                                return (
                                                    <Linha key={i} label={rotulo}>
                                                        {rotulo === 'Link'
                                                            ? <a href={valor} target="_blank" rel="noopener noreferrer">{valor}</a>
                                                            : valor}
                                                        {rotulo === 'Linha digitável' && (
                                                            <button type="button" className="fin-pp__btn fin-pp__btn--sm"
                                                                style={{ marginLeft: 8 }} title="Copiar a linha digitável"
                                                                onClick={function() { copiarLinhaDigitavel(valor); }}>
                                                                Copiar
                                                            </button>
                                                        )}
                                                    </Linha>
                                                );
                                            })}
                                            {mostrarAdiantamentoNaConta(parcela.adiantamento_fornecedor) && (
                                                <Linha label="Adiantamento do cliente">
                                                    <span style={{ color: '#0369a1' }}>
                                                        {parteAdiantamentoDaConta(parcela.adiantamento_fornecedor) !== null
                                                            ? <>🟠 − R$ {moeda(parteAdiantamentoDaConta(parcela.adiantamento_fornecedor))} nesta parcela</>
                                                            : parcela.adiantamento_fornecedor.sem_parcela
                                                                ? <>🟠 R$ {moeda(parcela.adiantamento_fornecedor.valor)} do fornecedor, ainda sem parcela (não descontado aqui)</>
                                                                : <>🟠 − R$ {moeda(parcela.adiantamento_fornecedor.valor)} (total do fornecedor)</>}
                                                    </span>
                                                    <div className="fin-pp__cell-muted">
                                                        Já debitado da conta do evento ·{' '}
                                                        <a href={'/financeiroSaldoAdiantamento' + (parcela.titulo?.evento?.id ? '?evento=' + parcela.titulo.evento.id : '')}>
                                                            ver extrato
                                                        </a>
                                                    </div>
                                                </Linha>
                                            )}
                                            {!situacaoPaga && parteAdiantamentoDaConta(parcela.adiantamento_fornecedor) !== null && (
                                                <Linha label="A pagar (líquido)">
                                                    <strong style={{ color: '#059669' }}>
                                                        R$ {moeda(Math.max(0, (parseFloat(parcela.valor) || 0) - parteAdiantamentoDaConta(parcela.adiantamento_fornecedor)))}
                                                    </strong>
                                                    {Math.max(0, (parseFloat(parcela.valor) || 0) - parteAdiantamentoDaConta(parcela.adiantamento_fornecedor)) <= 0 && (
                                                        <div className="fin-pp__cell-muted">
                                                            Coberta pelo adiantamento — continua em aberto até você dar a baixa (Pagar).
                                                        </div>
                                                    )}
                                                </Linha>
                                            )}
                                            {situacaoPaga && (<Linha label="Pago em">{parcela.pagamento ? moment(parcela.pagamento).format('DD/MM/YYYY') : '—'}</Linha>)}
                                            {situacaoPaga && (<Linha label="Caixa / Cartão">{parcela.caixa?.nome || '—'}</Linha>)}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </section>

                    {/* ── Rastreio ──────────────────────────────────────────────── */}
                    <section className="fin-pp__region" style={{ marginBottom: 18 }}>
                        <div className="fin-pp__cphead">
                            <span className="fin-pp__cphead-ic"><FaHistory size={18} /></span>
                            <div>
                                <h2 className="fin-pp__cphead-title">Rastreio</h2>
                                <p className="fin-pp__cphead-sub">Quem liberou, quem aprovou e quando</p>
                            </div>
                        </div>
                        <div className="fin-pp__card" style={{ overflow: 'hidden' }}>
                            <div className="fin-pp__tblwrap">
                                <table className="fin-pp__tbl fin-pp__tbl--kv">
                                    <tbody>
                                        <Linha label="Criada em">{parcela.created_at ? moment(parcela.created_at).format('DD/MM/YYYY HH:mm') : '—'}</Linha>
                                        <Linha label="Liberada para o Financeiro">
                                            {parcela.liberado_financeiro_at
                                                ? <>{moment(parcela.liberado_financeiro_at).format('DD/MM/YYYY HH:mm')}
                                                    {parcela.liberado_financeiro_por ? <div className="fin-pp__cell-muted">por {parcela.liberado_financeiro_por}</div> : null}</>
                                                : <span className="fin-pp__cell-muted">Ainda não liberada</span>}
                                        </Linha>
                                        {parcela.travado_pagamento_em && (
                                            // 07/10: o Financeiro avisou (e-mail aos responsáveis) que ia pagar
                                            <Linha label="Travada para pagamento">
                                                <span><FaLock size={11} /> {moment(parcela.travado_pagamento_em).format('DD/MM/YYYY HH:mm')}</span>
                                                {parcela.travado_pagamento_por ? <div className="fin-pp__cell-muted">por {parcela.travado_pagamento_por} · responsáveis do evento avisados por e-mail</div> : null}
                                            </Linha>
                                        )}
                                        {parcela.alerta_orcamento_em && (
                                            <Linha label="Orçamento alterado depois">
                                                <span style={{ color: 'var(--fin-dg)', fontWeight: 600 }}>⚠ {parcela.alerta_orcamento_texto || 'Orçamento alterado'}</span>
                                                <div className="fin-pp__cell-muted">{moment(parcela.alerta_orcamento_em).format('DD/MM/YYYY HH:mm')}{parcela.alerta_orcamento_por ? ' · por ' + parcela.alerta_orcamento_por : ''}</div>
                                            </Linha>
                                        )}
                                        {parcela.operador?.nome && (<Linha label="Operador">{parcela.operador.nome}</Linha>)}
                                        {isAprovada && (
                                            <Linha label="Aprovação do diretor">
                                                {moment(parcela.dataAprovacaoDiretor).format('DD/MM/YYYY HH:mm')}
                                                {parcela.aprovado_diretor_por?.nome ? <div className="fin-pp__cell-muted">por {parcela.aprovado_diretor_por.nome}</div> : null}
                                            </Linha>
                                        )}
                                        {isReprovada && (
                                            <Linha label="Reprovação do diretor">
                                                {moment(parcela.dataReprovacaoDiretor).format('DD/MM/YYYY HH:mm')}
                                                {parcela.reprovado_diretor_por?.nome ? <div className="fin-pp__cell-muted">por {parcela.reprovado_diretor_por.nome}</div> : null}
                                            </Linha>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </section>

                    {/* ── Observações do Eventos (Rafael 07/10) ─────────────────────
                        Tudo o que o operador escreveu em "observação": Previsão, Efetivo e linhas
                        do orçamento deste fornecedor. Só aparece quando há o que mostrar. */}
                    {(parcela.observacoes_eventos || []).length > 0 && (
                        <section className="fin-pp__region" style={{ marginBottom: 18 }} data-observacoes-eventos>
                            <div className="fin-pp__cphead">
                                <span className="fin-pp__cphead-ic"><FaRegCommentDots size={18} /></span>
                                <div>
                                    <h2 className="fin-pp__cphead-title">Observações do Eventos</h2>
                                    <p className="fin-pp__cphead-sub">O que o operador escreveu na Previsão, no Efetivo e no orçamento deste fornecedor</p>
                                </div>
                            </div>
                            <div className="fin-pp__card" style={{ overflow: 'hidden' }}>
                                <div className="fin-pp__tblwrap">
                                    <table className="fin-pp__tbl fin-pp__tbl--kv">
                                        <tbody>
                                            {parcela.observacoes_eventos.map((o, i) => (
                                                <Linha key={i} label={o.origem}><span style={{ whiteSpace: 'pre-wrap' }}>{o.texto}</span></Linha>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </section>
                    )}

                    {/* ── Histórico ─────────────────────────────────────────────────
                        O Rastreio acima lê colunas soltas da própria parcela (criação,
                        liberação, aprovação do diretor). Isto aqui é a linha do tempo do
                        que ACONTECEU com ela depois — inclusive baixa feita sem dados de
                        pagamento, que antes não deixava rastro nenhum.
                        Só aparece quando há o que mostrar: seção vazia em conta nova só
                        ocuparia espaço. */}
                    {historico.length > 0 && (
                        <section className="fin-pp__region" style={{ marginBottom: 18 }}>
                            <div className="fin-pp__cphead">
                                <span className="fin-pp__cphead-ic"><FaHistory size={18} /></span>
                                <div>
                                    <h2 className="fin-pp__cphead-title">Histórico</h2>
                                    <p className="fin-pp__cphead-sub">O que aconteceu com esta conta, do mais antigo ao mais recente</p>
                                </div>
                            </div>
                            <div className="fin-pp__card" style={{ overflow: 'hidden' }}>
                                <div className="fin-pp__tblwrap">
                                    <table className="fin-pp__tbl">
                                        <thead>
                                            <tr>
                                                <th style={{ width: 150 }}>Quando</th>
                                                <th>O que aconteceu</th>
                                                <th style={{ width: 190 }}>Quem</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {historico.map((h) => (
                                                <tr key={h.id}>
                                                    <td style={{ whiteSpace: 'nowrap' }}>
                                                        {h.data ? moment(h.data).format('DD/MM/YYYY HH:mm') : '—'}
                                                    </td>
                                                    <td>
                                                        {h.sem_dados_pagamento && (
                                                            <span className="fin-pp__minitag" style={{ background: '#fdf3e2', color: '#8a5206', border: '1px solid #e8c88a', marginRight: 6 }}>
                                                                ⚠️ sem dados
                                                            </span>
                                                        )}
                                                        {h.descricao}
                                                    </td>
                                                    <td className="fin-pp__cell-muted">{h.usuario_nome || '—'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </section>
                    )}

                    {/* ── Documentos ────────────────────────────────────────────── */}
                    <section className="fin-pp__region">
                        <div className="fin-pp__cphead">
                            <span className="fin-pp__cphead-ic"><FaRegFileAlt size={18} /></span>
                            <div>
                                <h2 className="fin-pp__cphead-title">Documentos</h2>
                                <p className="fin-pp__cphead-sub">Invoice, nota fiscal, boleto, comprovante e recibos anexados</p>
                            </div>
                        </div>
                        <div className="fin-pp__card" style={{ overflow: 'hidden' }}>
                            {(() => {
                                // Fase 2 do PLANO-documentos-multiplos: lista TODOS os
                                // documentos por tipo. Sem o endpoint (deploy em curso),
                                // cai nos botões legados de documento único.
                                const tipos = [
                                    { key: 'notafiscal', rotulo: 'Nota Fiscal', icone: <FaFileAlt size={13} /> },
                                    { key: 'boleto', rotulo: 'Boleto', icone: <FaFileInvoice size={13} /> },
                                    { key: 'comprovantepagamento', rotulo: 'Comprovante', icone: <FaFileInvoiceDollar size={13} /> },
                                    { key: 'reciboadiantamento', rotulo: 'Recibo', icone: <FaReceipt size={13} /> },
                                ];
                                const temLegado = !!(parcela.arquivoboleto?.id || parcela.arquivoboleto
                                    || parcela.arquivonotafiscal?.id || parcela.arquivonotafiscal
                                    || parcela.arquivocomprovantepagamento?.id || parcela.arquivocomprovantepagamento
                                    || parcela.arquivoreciboadiantamento?.id || parcela.arquivoreciboadiantamento);
                                const botoesLegado = () => (
                                    <div className="d-flex flex-wrap gap-2" style={{ padding: 14 }}>
                                        {(parcela.arquivoboleto?.id || parcela.arquivoboleto) && (
                                            <FormButton method="get" title="Boleto" label="Boleto" action={arquivoApi + parcela.id} icon={<FaFileInvoice size={14} />} color="outline-primary" data={{ token: token, tipoArquivo: 'boleto' }} />)}
                                        {(parcela.arquivonotafiscal?.id || parcela.arquivonotafiscal) && (
                                            <FormButton method="get" title="Nota Fiscal" label="Nota Fiscal" action={arquivoApi + parcela.id} icon={<FaFileAlt size={14} />} color="outline-primary" data={{ token: token, tipoArquivo: 'notafiscal' }} />)}
                                        {(parcela.arquivocomprovantepagamento?.id || parcela.arquivocomprovantepagamento) && (
                                            <FormButton method="get" title="Comprovante" label="Comprovante" action={arquivoApi + parcela.id} icon={<FaFileInvoiceDollar size={14} />} color="outline-primary" data={{ token: token, tipoArquivo: 'comprovantepagamento' }} />)}
                                        {(parcela.arquivoreciboadiantamento?.id || parcela.arquivoreciboadiantamento) && (
                                            <FormButton method="get" title="Recibo de Adiantamento" label="Recibo" action={arquivoApi + parcela.id} icon={<FaReceipt size={14} />} color="outline-primary" data={{ token: token, tipoArquivo: 'reciboadiantamento' }} />)}
                                    </div>
                                );
                                // Invoice do fornecedor pago no exterior (01/10): vem do plano (anexada pelo Eventos na
                                // Previsão) e vale como NF e dados de pagamento. Só baixa — quem troca é o Eventos.
                                const tipoInvoice = { key: 'invoice', rotulo: 'Invoice', icone: <FaFileInvoice size={13} /> };
                                const invoices = Array.isArray(parcela.invoices) ? parcela.invoices : [];
                                const temListaDocs = !!(arquivosParcela && tipos.some(t => (arquivosParcela[t.key] || []).length > 0));
                                const temLista = temListaDocs || invoices.length > 0;
                                if (temLista) {
                                    // Dado tabular = tabela .fin-pp__tbl (regra da skill).
                                    // Antes era uma pilha de divs com borda tracejada.
                                    const linhas = invoices.map(inv => ({
                                        tipo: tipoInvoice, invoice: true, total: invoices.length,
                                        doc: { id: inv.id, nome: inv.nome, arquivo_id: inv.arquivo_id, created_at: inv.enviado_em },
                                    }));
                                    if (temListaDocs) tipos.forEach(t => {
                                        const docs = arquivosParcela[t.key] || [];
                                        docs.forEach(d => linhas.push({ tipo: t, doc: d, total: docs.length }));
                                    });
                                    return (<>
                                        <div className="fin-pp__tblwrap">
                                            <table className="fin-pp__tbl">
                                                <thead>
                                                    <tr>
                                                        <th style={{ width: '22%' }}>Tipo</th>
                                                        <th>Documento</th>
                                                        <th style={{ width: '20%' }}>Enviado em</th>
                                                        <th style={{ width: '18%' }}>Ações</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {linhas.map(l => (
                                                        <tr key={l.tipo.key + '_' + l.doc.id}>
                                                            <td className="fin-pp__cell-muted" style={{ whiteSpace: 'nowrap' }}>
                                                                {l.tipo.icone} {l.doc.rotulo_tipo || l.tipo.rotulo}
                                                            </td>
                                                            <td className="fin-pp__cell-strong fin-pp__ellipsis" title={l.doc.nome}>
                                                                {l.doc.nome}
                                                                {l.doc.principal && l.total > 1 && (
                                                                    <span className="fin-pp__minitag is-info" style={{ marginLeft: 6 }}>principal</span>
                                                                )}
                                                            </td>
                                                            <td className="fin-pp__cell-muted" style={{ whiteSpace: 'nowrap' }}>
                                                                {l.doc.created_at ? moment(l.doc.created_at).format('DD/MM/YYYY HH:mm') : '—'}
                                                            </td>
                                                            <td>
                                                                <div className="d-flex gap-1">
                                                                    <button type="button" className="fin-pp__btn fin-pp__btn--sm"
                                                                        onClick={() => window.open(process.env.REACT_APP_API_URL + '/arquivo/download/' + l.doc.arquivo_id, '_blank')}>
                                                                        Baixar
                                                                    </button>
                                                                    {l.invoice ? (
                                                                        <span className="fin-pp__minitag is-info" title="Anexada pelo Eventos na Previsão de Pagamento">do Eventos</span>
                                                                    ) : (
                                                                    <button type="button" className="fin-pp__btn fin-pp__btn--sm fin-pp__btn--danger-ghost"
                                                                        title={'Remover ' + l.tipo.rotulo} aria-label={'Remover ' + l.tipo.rotulo}
                                                                        onClick={() => acaoRemoverArquivo(l.doc, l.tipo.rotulo)}>
                                                                        ✕
                                                                    </button>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                        {!temListaDocs && temLegado && botoesLegado()}
                                    </>);
                                }
                                if (!temLegado) {
                                    return <div className="fin-pp__empty" style={{ padding: '26px 0' }}>Nenhum documento anexado.</div>;
                                }
                                return botoesLegado();
                            })()}
                        </div>
                    </section>
                </>)}
            </div>

            {/* ── Modal de confirmação / aviso (.fin-pp__modal) ─────────────────
                Sem `onOk` o modal é só aviso e mostra apenas "Entendi". */}
            {!!modalConfirm && (
                <div className="modal fade show d-block fin-pp fin-pp__modalbg"
                    onClick={(e) => { if (e.target === e.currentTarget) setModalConfirm(null); }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content fin-pp__modal">
                            <div className="fin-pp__modal-head">
                                <span className={'fin-pp__modal-head-ic' + (modalConfirm.tom === 'danger' ? ' is-danger' : '')}>
                                    <FaExclamationTriangle size={16} />
                                </span>
                                <h3 className="fin-pp__modal-title">
                                    {modalConfirm.titulo}
                                    {modalConfirm.sub ? <span className="fin-pp__modal-sub">{modalConfirm.sub}</span> : null}
                                </h3>
                                <button type="button" className="fin-pp__modal-close" onClick={() => setModalConfirm(null)}>
                                    <FaTimes size={16} />
                                </button>
                            </div>
                            <div className="modal-body" style={{ fontSize: 13.5, color: 'var(--fin-tx)', lineHeight: 1.6 }}>
                                {modalConfirm.corpo}
                            </div>
                            <div className="fin-pp__modal-foot">
                                <button type="button" className="fin-pp__btn" onClick={() => setModalConfirm(null)}>
                                    {modalConfirm.onOk ? 'Cancelar' : 'Entendi'}
                                </button>
                                {modalConfirm.onOk && (
                                    <button type="button"
                                        className={'fin-pp__btn ' + (modalConfirm.tom === 'danger' ? 'fin-pp__btn--danger' : 'fin-pp__btn--primary')}
                                        onClick={() => { const fn = modalConfirm.onOk; setModalConfirm(null); fn(); }}>
                                        {modalConfirm.labelOk || 'Confirmar'}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

        </ErpTemplate>
    );
}
